import React, { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Building2, LayoutGrid, Lock, Mail, School } from 'lucide-react'
import tenantSignupService, { SIGNUP_EMAIL_KEY } from '@/services/tenantSignupService'
import { buildTenantAppRedirectUrl } from '@/utils/tenantRuntime'
import AuthLayout, { StepsCard } from '@/components/auth/capabble/AuthLayout'
import { Button, Callout, Checkbox, StrengthMeter, TextField } from '@/components/auth/capabble/controls'
import { getPasswordStrength, MISMATCH_ERROR, PASSWORD_ERROR, PASSWORD_RULE } from '@/components/auth/capabble/password'

function toSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

type Field = 'udiseCode' | 'name' | 'email' | 'password' | 'confirm'
type Errors = Partial<Record<Field, string>>
const FIELD_ORDER: Field[] = ['udiseCode', 'name', 'email', 'password', 'confirm']

const GENERAL_ERROR = "We couldn't create the account. Check your connection and try again."

const Signup: React.FC = () => {
  const [udiseCode, setUdiseCode] = useState('')
  const [nameOfSchool, setNameOfSchool] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPasswords, setShowPasswords] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Errors>({})
  const [generalError, setGeneralError] = useState<string | null>(null)

  const refs = {
    udiseCode: useRef<HTMLInputElement>(null),
    name: useRef<HTMLInputElement>(null),
    email: useRef<HTMLInputElement>(null),
    password: useRef<HTMLInputElement>(null),
    confirm: useRef<HTMLInputElement>(null),
  }

  const strength = useMemo(() => getPasswordStrength(password), [password])
  const slug = useMemo(() => toSlug(udiseCode.trim() || nameOfSchool.trim()), [udiseCode, nameOfSchool])

  const clearError = (field: Field) => {
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }))
  }

  const showErrors = (next: Errors) => {
    setErrors(next)
    const first = FIELD_ORDER.find((f) => next[f])
    // Focus after the error renders so it is announced with the field.
    if (first) requestAnimationFrame(() => refs[first].current?.focus())
  }

  const validate = (): Errors => {
    const next: Errors = {}
    if (!/^\d{11}$/.test(udiseCode)) next.udiseCode = 'Enter the 11-digit UDISE+ code'
    if (!nameOfSchool.trim() || !slug) next.name = 'Enter your institution name'
    if (!/^\S+@\S+\.\S+$/.test(adminEmail.trim())) next.email = 'Enter an email address like name@yourschool.in'
    if (strength < 2) next.password = PASSWORD_ERROR
    else if (confirmPassword !== password) next.confirm = MISMATCH_ERROR
    return next
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setGeneralError(null)

    const next = validate()
    if (Object.keys(next).length) {
      showErrors(next)
      return
    }
    setErrors({})

    const email = adminEmail.trim().toLowerCase()
    setSubmitting(true)
    try {
      const result = await tenantSignupService.startSignup({
        udiseCode: udiseCode.trim(),
        name: nameOfSchool.trim(),
        slug,
        adminEmail: email,
        adminPassword: password,
        confirmPassword,
      })
      localStorage.setItem('tenantSlug', result.tenantSlug)
      try {
        sessionStorage.setItem(SIGNUP_EMAIL_KEY, email)
      } catch {
        /* storage unavailable: the confirm page says "your email" */
      }
      // A full load with ?tenant=<new school> so a leftover ?tenant= from another school
      // can't make the verify page confirm against the wrong school.
      window.location.assign(buildTenantAppRedirectUrl(result.tenantSlug, result.ticket))
    } catch (err: unknown) {
      const response =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number; data?: { message?: string; errorCode?: string } } }).response
          : undefined
      const code = response?.data?.errorCode
      if (code === 'slug_taken') {
        showErrors({ udiseCode: 'This UDISE+ code already has an account. Sign in instead. If you never confirmed your email, signing in sends a new code.' })
      } else if (code === 'admin_email_taken') {
        showErrors({ email: 'This email address already has an account. Sign in instead. If you never confirmed it, signing in sends a new code.' })
      } else if (response?.status && response.status < 500 && response.data?.message) {
        setGeneralError(response.data.message)
      } else {
        setGeneralError(GENERAL_ERROR)
      }
    } finally {
      setSubmitting(false)
    }
  }

  // No maxLength on the inputs: it would cut a pasted "12 345" before the spaces are stripped.
  const digitsOnly = (value: string, max: number) => value.replace(/\D/g, '').slice(0, max)
  const passwordType = showPasswords ? 'text' : 'password'

  return (
    <AuthLayout
      layout="signup"
      headline={
        <>
          Set up your school <span className="cb-auth-accent">once.</span>
        </>
      }
      subline="One workspace account opens every module your school uses."
      card={
        <StepsCard
          title="Three steps to get started"
          items={[
            { icon: Building2, title: 'Add your institution', detail: 'UDISE+ code and name' },
            { icon: Mail, title: 'Confirm your email', detail: 'We email you a 6-digit code' },
            { icon: LayoutGrid, title: 'Open any module', detail: 'Exams, exam centre, fees and more' },
          ]}
        />
      }
    >
      <div className="cb-auth-head">
        <h1 className="cb-auth-title">Create your workspace account</h1>
        <p className="cb-auth-subtitle">
          Set up your institution once and use the same Capabble account in every module your school has turned on.
        </p>
      </div>

      <form className="cb-auth-form" style={{ marginTop: 0 }} noValidate onSubmit={handleSubmit}>
        <div className="cb-auth-section">
          <p className="cb-auth-section-label">Institution</p>
          <TextField
            ref={refs.udiseCode}
            id="udiseCode"
            label="UDISE+ code"
            hint="11 digits"
            error={errors.udiseCode}
            inputMode="numeric"
            autoComplete="off"
            required
            value={udiseCode}
            onChange={(e) => {
              setUdiseCode(digitsOnly(e.target.value, 11))
              clearError('udiseCode')
            }}
          />
          <TextField
            ref={refs.name}
            id="nameOfSchool"
            label="Institution name"
            icon={School}
            placeholder="As it appears on UDISE+"
            error={errors.name}
            autoComplete="organization"
            required
            value={nameOfSchool}
            onChange={(e) => {
              setNameOfSchool(e.target.value)
              clearError('name')
            }}
          />
        </div>

        <div className="cb-auth-section">
          <p className="cb-auth-section-label">Your sign-in</p>
          <TextField
            ref={refs.email}
            id="adminEmail"
            label="Email address"
            icon={Mail}
            type="email"
            placeholder="name@yourschool.in"
            hint="You'll sign in with this and we'll email a 6-digit code to it to confirm your account."
            error={errors.email}
            autoComplete="email"
            required
            value={adminEmail}
            onChange={(e) => {
              setAdminEmail(e.target.value)
              clearError('email')
            }}
          />
          <div>
            <TextField
              ref={refs.password}
              id="password"
              label="Create password"
              icon={Lock}
              type={passwordType}
              hint={PASSWORD_RULE}
              error={errors.password}
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                clearError('password')
              }}
            />
            {password ? (
              <div style={{ marginTop: 10 }}>
                <StrengthMeter level={strength} />
              </div>
            ) : null}
          </div>
          <TextField
            ref={refs.confirm}
            id="confirmPassword"
            label="Confirm password"
            icon={Lock}
            type={passwordType}
            error={errors.confirm}
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value)
              clearError('confirm')
            }}
          />
          <div className="cb-auth-checks">
            <Checkbox label="Show passwords" checked={showPasswords} onChange={setShowPasswords} />
          </div>
        </div>

        {generalError ? <Callout>{generalError}</Callout> : null}

        <Button type="submit" iconRight={ArrowRight} loading={submitting} loadingLabel="Creating account">
          Create account
        </Button>
      </form>

      <p className="cb-auth-alt">
        Already have an account? <Link to="/login">Sign in</Link>
      </p>
    </AuthLayout>
  )
}

export default Signup
